import {
  type Dispatch,
  type FC,
  type ForwardedRef,
  forwardRef,
  type ReactNode,
  useCallback,
  useId,
  useImperativeHandle,
  useRef,
} from "react";

import {
  areReferencedNodeValuesEqual,
  isReferencedNodeValue,
  isTriggerNode,
  type NodeModelWithId,
} from "@doitintl/cloudflow-commons";
import { type ReferencedNodeValue, type UnwrappedApiServiceModelDescriptor } from "@doitintl/cmp-models";
import { FormControl, FormHelperText, InputLabel, OutlinedInput, Stack, styled } from "@mui/material";

import { useToggle } from "../../../../../../../utils/useToggle";
import { getInitialValueForModel } from "../../../useApiActionParametersSchema";
import { type FieldCommonProps } from "../../../useFieldCommonProps";
import { ReferencedFieldAddButton } from "./ReferencedFieldAddButton";
import { ReferencedFieldChip } from "./ReferencedFieldChip";
import { ReferencedFieldMenu } from "./ReferencedFieldMenu";
import { useReferencedFieldContext } from "./useReferencedFieldContext";
import { type MenuItemData, useReferencedFieldMenuItems } from "./useReferencedFieldMenuItems";

export type ReferencedFieldWrapperFieldProps = Omit<FieldCommonProps, "autoComplete">;

export const Wrapper = styled(Stack)(({ theme }) => ({
  flexFlow: "row nowrap",
  minWidth: 0,
  flexGrow: 1,
  ".MuiFormControl-root:not(:last-child) .MuiOutlinedInput-root": {
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
  },
  "&:hover .MuiInputBase-root:not(.Mui-focused):not(.Mui-error):not(.Mui-disabled) .MuiOutlinedInput-notchedOutline, &:hover button:not(.Mui-disabled)":
    {
      borderColor: theme.palette.action.active,
    },
}));

export const ReferencedFieldWrapper: FC<{
  commonFieldProps: ReferencedFieldWrapperFieldProps;
  children?: ReactNode;
  disabled?: boolean;
  model: UnwrappedApiServiceModelDescriptor;
  rootReferencedNodeValue?: ReferencedNodeValue;
  hideVariableReference?: boolean;
}> = forwardRef((props, forwardedRef: ForwardedRef<HTMLDivElement>) => {
  const {
    children,
    commonFieldProps,
    disabled,
    model,
    rootReferencedNodeValue,
    hideVariableReference,
    ...forwardedProps
  } = props;
  const [isMenuShown, showMenu, hideMenu] = useToggle(false);
  const { referenceableNodes } = useReferencedFieldContext();
  const inputId = useId();
  const helperTextId = useId();
  const anchorEl = useRef<HTMLDivElement>(null);

  useImperativeHandle(forwardedRef, () => anchorEl.current!);

  const { referencedNodeId }: ReferencedNodeValue = commonFieldProps.value || {};
  const triggerNode = referencedNodeId
    ? (referenceableNodes as unknown as NodeModelWithId[]).find(isTriggerNode)
    : null;
  const isCurrentNodeATrigger = triggerNode?.id === referencedNodeId;

  const { menuItems, currentlyDisplayedPath } = useReferencedFieldMenuItems(
    commonFieldProps.value,
    commonFieldProps.name,
    hideVariableReference
  );

  const setFieldValue = useCallback(
    (value: typeof commonFieldProps.value) => {
      commonFieldProps.onChange({
        target: {
          value,
          name: commonFieldProps.name,
        },
      });
    },
    [commonFieldProps]
  );

  const triggerOnBlur = useCallback(() => {
    commonFieldProps.onBlur({
      target: {
        name: commonFieldProps.name,
      },
    });
  }, [commonFieldProps]);

  const onAppendMenuItem: Dispatch<MenuItemData> = (menuItem) => {
    let value = commonFieldProps.value;
    if (menuItem.type === "node") {
      const isTrigger = isTriggerNode(
        referenceableNodes.find(({ id }) => id === menuItem.nodeId) as unknown as NodeModelWithId
      );
      value = {
        referencedField: isTrigger && menuItem.name === "Variables" ? ["variables"] : [], // INFO: when user selects variables node we jump right to the variables field
        referencedNodeId: menuItem.nodeId,
      };
    } else if (isReferencedNodeValue(value)) {
      value = { ...value, referencedField: [...currentlyDisplayedPath, menuItem.name] };
    }

    if (!menuItem.hasContinuation) {
      hideMenu();
    }
    setFieldValue(value);
  };

  const onDeleteReferencedNodeValue = useCallback(() => {
    setFieldValue(rootReferencedNodeValue ?? getInitialValueForModel(model));
    triggerOnBlur();
  }, [model, rootReferencedNodeValue, setFieldValue, triggerOnBlur]);

  const onPopMenuItem = () => {
    if (isCurrentNodeATrigger && currentlyDisplayedPath.length === 1 && currentlyDisplayedPath[0] === "variables") {
      setFieldValue(rootReferencedNodeValue ?? getInitialValueForModel(model));
      return;
    }

    if (currentlyDisplayedPath.length > 0) {
      const referencedField = Array.from(currentlyDisplayedPath);
      referencedField.length -= 1;
      setFieldValue({ ...commonFieldProps.value, referencedField });
    } else {
      setFieldValue(rootReferencedNodeValue ?? getInitialValueForModel(model));
    }
  };

  const disallowValueTrimming = areReferencedNodeValuesEqual(commonFieldProps.value, rootReferencedNodeValue);

  if (referenceableNodes.length === 0 && !disabled) {
    return <>{children}</>;
  }
  return (
    <Wrapper ref={anchorEl} {...forwardedProps}>
      {isReferencedNodeValue(commonFieldProps.value) || children === undefined ? (
        <FormControl fullWidth error={commonFieldProps.error && !disabled}>
          <InputLabel required={commonFieldProps.required} htmlFor={inputId}>
            {commonFieldProps.label}
          </InputLabel>
          <OutlinedInput
            onClick={(event) => {
              if (event.nativeEvent.target instanceof Node && anchorEl.current?.contains(event.nativeEvent.target)) {
                showMenu();
              }
            }}
            size="small"
            id={inputId}
            label={commonFieldProps.label}
            required={commonFieldProps.required}
            error={commonFieldProps.error}
            value="&nbsp;"
            readOnly
            startAdornment={
              isReferencedNodeValue(commonFieldProps.value) ? (
                <ReferencedFieldChip
                  referenceableNodes={referenceableNodes}
                  value={commonFieldProps.value}
                  onDelete={disallowValueTrimming ? undefined : onDeleteReferencedNodeValue}
                  onChange={setFieldValue}
                />
              ) : null
            }
            aria-describedby={helperTextId}
            sx={{
              pr: "14px",
              cursor: "pointer",
            }}
            disabled={disabled}
            onBlur={triggerOnBlur}
          />
          {commonFieldProps.error && <FormHelperText id={helperTextId}>{commonFieldProps.helperText}</FormHelperText>}
        </FormControl>
      ) : (
        children
      )}
      <ReferencedFieldMenu
        open={isMenuShown}
        anchorEl={anchorEl.current}
        hideBackButton={disallowValueTrimming}
        onClose={() => {
          triggerOnBlur();
          hideMenu();
        }}
        menuItems={menuItems}
        onAppendMenuItem={onAppendMenuItem}
        onPopMenuItem={onPopMenuItem}
      />
      <ReferencedFieldAddButton disabled={disabled} onClick={showMenu} />
    </Wrapper>
  );
});

ReferencedFieldWrapper.displayName = "ReferencedFieldWrapper";
